import { Fragment, useState, type ReactNode } from "react";
import type { FacturaDeLote, LotePagoDetalle } from "../types/reviewItem";
import { formatMonto } from "../utils/formato";
import {
  CLASE_ESTADO_LOTE,
  ETIQUETA_ESTADO_LOTE,
  ETIQUETA_RESULTADO,
  aprobacionesVigentes,
  datosFacturaDeLote,
  diaHoraMontevideo,
  filasDetalleLote,
  noCierranDe,
  resumenConciliacion,
  textoAprobaciones,
  textoBancoVinculado,
  textoEvento,
} from "../utils/lotesPago";

/** El backend guarda el UPN: se muestra la parte antes del @. */
const usuarioCorto = (upn: string | null) => (upn ? upn.split("@")[0] : "—");

/**
 * Detalle de un lote de pago. [NUEVO 04/11/2026]
 *
 * Reusable a propósito: lo usan Control de pagos (Contaduría) y la página de
 * aprobación (Tarea 10). No sabe nada de roles: lo que se puede hacer llega
 * por props (`acciones` para el lote, `accionesFactura` para cada factura).
 *
 * Los proveedores que no cierran con el banco van primero y en rojo: es lo
 * único que hay que mirar antes de aprobar.
 */
export function DetalleLotePago({
  lote,
  acciones,
  accionesFactura,
}: {
  lote: LotePagoDetalle;
  /** Botones a nivel del lote (aprobar, rechazar, PDF...). */
  acciones?: ReactNode;
  /** Botones por factura (sacar, mover...). Sin esto, las facturas son de solo lectura. */
  accionesFactura?: (f: FacturaDeLote) => ReactNode;
}) {
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const filas = filasDetalleLote(lote);
  const conciliado = lote.conciliacion.length > 0;
  const noCierra = lote.cierra !== null && lote.cierra !== undefined && !Boolean(lote.cierra);

  function alternar(clave: string) {
    setAbiertos((prev) => {
      const next = new Set(prev);
      next.has(clave) ? next.delete(clave) : next.add(clave);
      return next;
    });
  }

  return (
    <div className="detalle-lote-pago">
      <div className="detalle-contexto">
        <span>
          <b>{lote.nombre}</b>{" "}
          <span className={`badge ${CLASE_ESTADO_LOTE[lote.estado]}`}>{ETIQUETA_ESTADO_LOTE[lote.estado]}</span>
        </span>
        <span>
          <b>Total:</b> {lote.moneda} {formatMonto(lote.total)}
        </span>
        <span>
          {lote.cantidad_facturas} factura(s) · {lote.cantidad_proveedores} proveedor(es)
        </span>
        <span>
          <b>PDF del banco:</b> {textoBancoVinculado(lote)}
        </span>
        <span>
          <b>Armó:</b> {usuarioCorto(lote.creado_por)} ({diaHoraMontevideo(lote.creado_at)})
        </span>
      </div>

      {lote.motivo_rechazo ? (
        <p className="rejection-reason">
          <b>Motivo del rechazo:</b> {lote.motivo_rechazo}
        </p>
      ) : null}

      <p className={noCierra ? "detalle-lote-resumen error-text" : "detalle-lote-resumen"}>
        {resumenConciliacion(lote, conciliado ? noCierranDe(lote) : undefined)}
        {" · "}
        <b>Aprobaciones:</b> {textoAprobaciones(aprobacionesVigentes(lote))}
      </p>

      {acciones ? <div className="detalle-lote-acciones">{acciones}</div> : null}

      <table className="compact detalle-lote-tabla">
        <thead>
          <tr>
            <th>Proveedor</th>
            <th className="celda-monto">En la app</th>
            <th className="celda-monto">En el banco</th>
            <th className="celda-monto">Diferencia</th>
            <th>Resultado</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filas.length === 0 && (
            <tr>
              <td colSpan={6} className="empty-row">
                El lote no tiene facturas.
              </td>
            </tr>
          )}
          {filas.map((p) => (
            <Fragment key={p.clave}>
              <tr className={p.noCierra ? "fila-no-cierra" : undefined}>
                <td className="wrap-ok">
                  {p.nombre}
                  <span className="sub-dato mono">{p.card_code ?? "sin código"}</span>
                </td>
                <td className="mono celda-monto">{formatMonto(p.importe_app)}</td>
                <td className="mono celda-monto">{formatMonto(p.importe_banco)}</td>
                <td className="mono celda-monto">{formatMonto(p.diferencia)}</td>
                <td>
                  {p.resultado ? (
                    <span className={`badge ${p.noCierra ? "badge-rej" : "badge-auth"}`}>
                      {ETIQUETA_RESULTADO[p.resultado]}
                    </span>
                  ) : (
                    <span className="sin-resolver">Sin conciliar</span>
                  )}
                  {p.conFacturaNoHabilitada ? <span className="aviso-no-habilitada"> ⚠ ya no está habilitada</span> : null}
                </td>
                <td>
                  {p.facturas.length > 0 ? (
                    <button
                      className="btn-ghost"
                      aria-expanded={abiertos.has(p.clave)}
                      onClick={() => alternar(p.clave)}
                    >
                      {abiertos.has(p.clave) ? "Ocultar facturas" : `Ver facturas (${p.facturas.length})`}
                    </button>
                  ) : null}
                </td>
              </tr>
              {abiertos.has(p.clave) &&
                p.facturas.map((f) => {
                  const datos = datosFacturaDeLote(f);
                  return (
                    <tr key={`${f.trans_id}-${f.line_id}`} className="detalle-row">
                      <td className="mono wrap-ok">
                        {f.numero_comprobante ?? "sin número"}
                        <span className="sub-dato">Doc. SAP {datos.documento}</span>
                      </td>
                      <td className="mono celda-monto">{formatMonto(f.saldo)}</td>
                      <td colSpan={2}>
                        <span className="sub-dato">
                          Vence {datos.vencimiento} · Habilitó {datos.habilitadoPor}
                        </span>
                        {Boolean(f.habilitada) ? null : (
                          <span className="aviso-no-habilitada">⚠ ya no está habilitada para pagar</span>
                        )}
                      </td>
                      <td colSpan={2} className="actions-cell">
                        {accionesFactura ? accionesFactura(f) : null}
                      </td>
                    </tr>
                  );
                })}
            </Fragment>
          ))}
        </tbody>
      </table>

      <h3 className="detalle-lote-titulo">Historial</h3>
      {lote.eventos.length === 0 ? (
        <p className="sin-resolver">Sin movimientos.</p>
      ) : (
        <ul className="historial-lote">
          {lote.eventos.map((e, i) => (
            <li key={i}>
              <span className="mono">{diaHoraMontevideo(e.fecha)}</span> · {usuarioCorto(e.usuario)} · {textoEvento(e)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
